import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { Viewerlayer } from "../../../model/viewerlayer";
import { FormControl, FormGroup } from "@angular/forms";
import { LayerFeatureFilter } from "../../../model/layer-feature-filter";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { FeaturesSearchService } from "../../../services/internal-services/features-search.service";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { DamageAssessmentDialogComponent } from "../../../../dialog-components/damage-assessment-dialog/damage-assessment-dialog.component";
import { ApiViewerService } from "../../../services/internal-services/api-viewer.service";
import { MatDialog } from "@angular/material/dialog";
import { PropertyInformationDialogComponent } from "../../../../dialog-components/property-information-dialog/property-information-dialog.component";
import { MapService } from "../../../services/internal-services/map.service";
import { ReportService } from "../../../services/internal-services/report.service";
import { OverlayDialogComponent } from "../../../../dialog-components/overlay-dialog/overlay-dialog.component";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "phoenix-user-data-list",
  templateUrl: "./user-data-list.component.html",
  styleUrls: ["./user-data-list.component.scss"],
  standalone: false,
})
export class UserDataListComponent {
  viewerService = inject(VIEWER_SERVICE);

  private _layers: Viewerlayer[];

  private _selectedLayer: Viewerlayer = null;

  focusedLayer: Viewerlayer = null;

  focusedFeature: any = null;

  selectedFeature: any = null;

  get layers(): Viewerlayer[] {
    return this._layers;
  }

  get selectedLayer(): Viewerlayer {
    return this._selectedLayer;
  }

  set selectedLayer(value: Viewerlayer) {
    this._selectedLayer = value;
  }

  @Input()
  set layers(layers: Viewerlayer[]) {
    this._layers = layers;
    this.featuresServiceSearch.setLayers(this.layers);
    this.featuresServiceSearch.setPropertiesFromFeatures();
    this.featuresServiceSearch.setLayersNamesFromFeatures();
    this.featuresProperties = this.featuresServiceSearch.getProperties();
    this.layersNames = this.featuresServiceSearch.getLayersNames();
    this.layers.forEach((userLayer: Viewerlayer) => {
      this.viewerService.updateUserLayerStyle(userLayer);
    });
    this.clearSearchFilters();
  }

  @Input()
  set selectedFeatures(features: any[]) {
    if (features) {
      this.selectedLayer = this.layers.find((l) => l.name === features[0].layerName);
      if (this.selectedLayer) {
        this.selectedFeature = features[0];
      } else {
        // Getting layer selected by property/damage footprint
        this.getLayerByFootprint(features[0]);
      }
    }
  }

  @Output() onShowUploadDialog = new EventEmitter<any>();

  layersNames = null;

  featuresProperties = null;

  searchValue = "";

  filterSelectedLayer = "";

  filterSelectedField = "";

  selectedLayers: Set<Viewerlayer> = new Set();

  isMultiSelectMode = false;

  searchForm = new FormGroup({
    search: new FormControl(""),
    layer: new FormControl(this.filterSelectedLayer),
    field: new FormControl(this.filterSelectedField),
  });

  layerFeatureFilter: LayerFeatureFilter;

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected reportService: ReportService,
    protected mapService: MapService,
    protected featuresServiceSearch: FeaturesSearchService,
    protected apiViewerService: ApiViewerService,
    protected matDialog: MatDialog,
  ) {}

  changeUserLayerOrder(event) {
    moveItemInArray(this._layers, event.previousIndex, event.currentIndex);
    this.viewerService.updateUserOrder(this._layers);
  }

  clearSearchFilters(): void {
    this.filterSelectedField = "";
    this.filterSelectedLayer = "";
    this.searchForm.get("search").setValue("");
    this.featuresServiceSearch.restartFilters(this.layers);
    this.getPropertiesAndLayers(false);
  }

  clearText() {
    this.searchForm.get("search").setValue("");
    this.searchFeatures();
  }

  private getPropertiesAndLayers(reset = true) {
    this.featuresProperties = this.featuresServiceSearch.getProperties();
    this.layersNames = this.featuresServiceSearch.getLayersNames();

    if (this.filterSelectedLayer || this.filterSelectedField || this.searchValue) {
      this.layerFeatureFilter = {
        layerFilter: this.filterSelectedLayer,
        propertyFilter: this.filterSelectedField,
        searchValue: this.searchValue,
      };
    } else {
      this.layerFeatureFilter = null;
    }

    if (reset) {
      this.googleAnalyticsService.setGoogleAnalyticsEvent(
        GoogleAnalyticsConstantsEvents.DATA_SEARCH,
        GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
        true,
      );
    }
  }

  searchFeatures(): void {
    this.searchValue = this.searchForm.get("search").value;
    this._layers = this.featuresServiceSearch.filterUserLayersBySearch(this.searchValue);
    this.getPropertiesAndLayers();
    this._selectedLayer = null;
  }

  filterLayerFeatures(layer: string) {
    this.filterSelectedLayer = layer;
    this._layers = this.featuresServiceSearch.filterUserLayersByLayer(layer);
    this.getPropertiesAndLayers();
    this._selectedLayer = null;
  }

  filterFieldFeatures(field: string): void {
    this.filterSelectedField = field;
    this._layers = this.featuresServiceSearch.filterUserLayersByField(field);
    this.getPropertiesAndLayers();
    this._selectedLayer = null;
  }

  getLayerByFootprint(feature: any) {
    if (feature?.properties?.layerRef && feature?.properties?.featureRef) {
      this.selectedLayer = this.layers.find((l) => l.name === feature.properties.layerRef);
      this.selectedFeature = feature;
      /*this.selectedFeature = this.selectedLayer.source.features.features.find(
              (f) => f.id === feature?.properties?.featureRef
            );*/
    }
  }

  onFocusLayer($event: any) {
    this.focusedLayer = $event.layer;
    this.focusedFeature = $event.feature;
  }

  onSelectedLayer($event: any) {
    this.selectedLayer = $event;
  }

  onSelectedFeature($event: any) {
    this.selectedFeature = $event;
  }

  openUploadDialog() {
    this.onShowUploadDialog.emit(true);
  }

  inputSelectedFeature(layer, feature) {
    return feature?.layerName === layer.name ? feature : undefined;
  }

  openPropertyInformationDialog(data: any) {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_OPEN_PIF_X_PROPERTY_INFORMATION,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
    data.viewerService = this.viewerService;
    data.reportService = this.reportService;
    data.googleAnalyticsService = this.googleAnalyticsService;
    this.matDialog.open(PropertyInformationDialogComponent, {
      panelClass: "panel-dialog",
      id: "property-information-dialog",
      data: data,
      autoFocus: false,
    });
  }

  openDamageAssessmentDialog(layer: any) {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_OPEN_PIF_X_DAMAGE_ASSESSMENT,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
    const data = {
      layer,
      viewerService: this.viewerService,
      reportService: this.reportService,
      googleAnalyticsService: this.googleAnalyticsService,
      mapService: this.mapService,
    };

    this.matDialog.open(DamageAssessmentDialogComponent, {
      panelClass: "panel-dialog",
      id: "damage-assessment-dialog",
      data,
      autoFocus: false,
    });
  }

  anyFilterActive(): boolean {
    return !!this.searchForm.get("search").value || !!this.filterSelectedLayer || !!this.filterSelectedField;
  }

  onSelectedMultiLayer(layer: Viewerlayer): void {
    if (this.selectedLayers.has(layer)) {
      this.selectedLayers.delete(layer);
    } else {
      this.selectedLayers.add(layer);
    }
  }

  deleteSelectedLayers(): void {
    const dialog = this.matDialog.open(OverlayDialogComponent, {
      data: {
        text: "Are you sure you want to remove the selected layers?",
        isConfirmation: true,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedLayers.forEach((layer) => {
          this.viewerService.removeFeatureFootprints(layer?.source?.features?.features);
        });

        this.viewerService.removeUserLayers(Array.from(this.selectedLayers).map((layer) => layer.id));

        this.onSelectedFeature(null);
        this.onSelectedLayer(null);

        this.isMultiSelectMode = false;
        this.selectedLayers.clear();
      }
    });
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_REMOVE,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  toggleMultiSelectMode(): void {
    this.isMultiSelectMode = !this.isMultiSelectMode;
    if (!this.isMultiSelectMode) {
      this.selectedLayers.clear();
    }
  }
}
